import { useCallback, useEffect, useRef, useState } from 'react';

import { PAY_CARD_PROXY_ID_LENGTH } from '../proxyId';
import { type BarcodeDetectorLike, FORMATS, loadBarcodeDetector } from './loadBarcodeDetector';

/*
 * The detector's TYPE moved to `loadBarcodeDetector` along with the decision of which one to use.
 *
 * `BarcodeDetector` is not in `lib.dom` — a WICG proposal, shipped in Chromium and absent from WebKit — so
 * it was declared locally here, on the reasoning that this hook was its only caller. It no longer is: the
 * loader picks between the browser's own and the WebAssembly polyfill [decision 0137], and a shape two
 * modules rely on belongs with the one that produces it.
 */

export const SCAN_STATE = {
  /** Asking for the camera, or waiting for the first frame. */
  starting: 'starting',
  /** The camera is live and frames are being read. */
  scanning: 'scanning',
  /** A barcode decoded. `value` carries it. */
  found: 'found',
  /** The person, or the device policy, said no to the camera. */
  denied: 'denied',
  /** There is a camera but this browser cannot decode a barcode from it. */
  unsupported: 'unsupported',
  /** The camera exists but failed to open — in use by something else, or no device at all. */
  unavailable: 'unavailable',
} as const;
export type ScanState = (typeof SCAN_STATE)[keyof typeof SCAN_STATE];

export interface BarcodeScanner {
  readonly state: ScanState;
  /** The decoded proxy ID, only ever set alongside `found`. */
  readonly value: string | null;
  /** Attach to the `<video>` that shows the candidate what the camera sees. */
  readonly videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Drop a decoded value and look again — the "scan again" control. */
  readonly rescan: () => void;
}

/** How often a frame is examined. 250ms reads roughly four frames a second, which is plenty for a card
 * held still, and leaves the main thread alone the rest of the time — this runs on a cheap laptop. */
const FRAME_INTERVAL_MS = 250;

/**
 * How many frames in a row may fail before the scan gives up — two seconds of them.
 *
 * One bad frame is normal and means nothing. A RUN of them is a different fact: the detector is broken, not
 * the frame, and every further attempt is a camera light in a branch lobby recording nothing. The loader
 * catches the common cause before the camera is ever opened (it compiles the WebAssembly first), so this is
 * the backstop for a detector that starts working and then stops — a WebAssembly heap exhausted on a kiosk
 * that has been open all day, a canvas the browser decides is tainted. Two seconds is long enough that no
 * transient run of failures reaches it, and short enough that nobody stands there waiting.
 */
const MAX_CONSECUTIVE_FRAME_FAILURES = 8;

/**
 * Reads a pay card's barcode from the device camera [KPC-1 § *Webcam reads the barcode with focus assist*].
 *
 * **It reports what it cannot do instead of pretending.** `BarcodeDetector` ships in Chromium and not in
 * WebKit, and KPC-1 names BOTH form factors — *"Chromebook/cheap laptop **and** iPad both have webcams, so
 * the scan must work on either"*. On a Chromebook this decodes; on an iPad today it cannot, and the honest
 * outcome is `unsupported`, which the screen turns into "enter it by hand" and "ask a representative" —
 * the fallback the story already defines, not a new behaviour invented here. Silently showing a camera that
 * will never read anything would be the worst of the three: it looks like it is working.
 *
 * **Nothing is captured, stored or uploaded.** The stream is read frame by frame in memory and the tracks
 * are stopped on unmount. What leaves this hook is the decoded proxy ID and nothing else — no photo, no
 * recording — which is what KPC-1 § *nothing else from the card is captured or stored* requires.
 */
export function useBarcodeScanner(active: boolean): BarcodeScanner {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<ScanState>(SCAN_STATE.starting);
  const [value, setValue] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const rescan = useCallback(() => {
    // The reset lives in the gesture, not in the effect. Setting it inside the effect fires a second render
    // on every mount for a value that is already the initial one.
    setValue(null);
    setState(SCAN_STATE.starting);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!active) return;

    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let consecutiveFailures = 0;
    // Guards every async continuation below: an await that resolves after the modal closed would set state
    // on an unmounted component and, worse, leave a camera light on.
    let live = true;

    const stop = () => {
      live = false;
      if (timer !== undefined) window.clearInterval(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
    };

    const start = async () => {
      /*
       * Resolved before the camera is asked for, and the ORDER is deliberate: asking a candidate to allow a
       * camera and then telling them the device cannot decode anything is worse than not asking. On a
       * Windows device — a Surface, one of the two form factors [decision 0140] names — Chromium has no
       * `BarcodeDetector` and this resolves through the WebAssembly polyfill instead.
       *
       * `null` here means "no scan is possible on this device", and it now covers a WebAssembly that cannot
       * RUN as well as one that cannot load: the loader compiles the binary before handing the detector
       * over, because a detector that resolves and then throws on every frame is the failure this hook
       * cannot see — the `catch` below is written for one bad frame, not for a permanent one.
       */
      const Detector = await loadBarcodeDetector();
      if (!live) return;
      if (Detector === null) {
        setState(SCAN_STATE.unsupported);
        return;
      }
      /*
       * Constructed BEFORE the camera, for the same reason the detector is resolved before it: the ponyfill
       * validates the format names in its constructor and throws on one it does not know, and a detector
       * that cannot be constructed is the same fact to the candidate as a browser that cannot decode. It
       * used to be built after `getUserMedia`, where a throw left the camera on and the screen saying
       * "scanning" with no timer running.
       */
      let detector: BarcodeDetectorLike;
      try {
        detector = new Detector({ formats: [...FORMATS] });
      } catch {
        setState(SCAN_STATE.unsupported);
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // The candidate holds the card up to the screen they are standing at, so the front camera is the
          // one pointing at them. `ideal` rather than `exact` so a device with only one camera still opens.
          video: { facingMode: { ideal: 'user' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      } catch (error) {
        if (!live) return;
        // A refusal and a missing camera are different things to tell someone: one is "allow the camera",
        // the other is "this kiosk has no camera, ask a representative".
        const name = error instanceof DOMException ? error.name : '';
        setState(name === 'NotAllowedError' ? SCAN_STATE.denied : SCAN_STATE.unavailable);
        return;
      }
      if (!live) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }

      /*
       * "Focus assist", as far as the web platform allows it: continuous autofocus is a non-standard track
       * constraint that Chromium honours and everything else ignores. A card held at arm's length is right
       * at the near limit of a laptop webcam, so it is worth asking for — and worth not failing over.
       */
      const [track] = stream.getVideoTracks();
      try {
        // `focusMode` is a non-standard track constraint and is not in `lib.dom`, hence the cast. Asked
        // for, never depended on — the catch below is the supported path on every browser that ignores it.
        await track?.applyConstraints({
          advanced: [{ focusMode: 'continuous' }],
        } as unknown as MediaTrackConstraints);
      } catch {
        /* Not supported here. The scan still works, it just focuses the way the driver decides. */
      }

      const video = videoRef.current;
      if (video === null || !live) return;
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      if (!live) return;
      setState(SCAN_STATE.scanning);

      timer = window.setInterval(() => {
        void (async () => {
          const el = videoRef.current;
          if (el === null || el.readyState < 2) return;
          try {
            const codes = await detector.detect(el);
            consecutiveFailures = 0;
            // The backend validates too; this only refuses to report a read that cannot be a proxy ID, so
            // a stray non-numeric code in frame does not end the scan. The format rule itself (16 digits,
            // the prefix) is `ScanOverlay`'s, which reports any other read as a failed read [KPC-1c].
            const reads = codes
              .map((code) => code.rawValue?.trim())
              .filter((read): read is string => read !== undefined && /^[0-9]{6,32}$/.test(read));
            /*
             * ⚠️ **Every code in the frame, a 16-digit one first.** It read `codes[0]` alone, so the packet's
             * own UPC-A, decoded first beside the proxy barcode, was the read — and since KPC-1c that closes
             * the layer as a failed read with the proxy id in plain view. Prefix-agnostic on purpose: the
             * prefix is the overlay's check, and 16 is the length both sides already hold [0181].
             */
            const raw = reads.find((read) => read.length === PAY_CARD_PROXY_ID_LENGTH) ?? reads[0];
            if (raw !== undefined && live) {
              setValue(raw);
              setState(SCAN_STATE.found);
              stop();
            }
          } catch {
            /*
             * One bad frame is not a failure — the next one is 250ms away. A RUN of them is, and it used to
             * be swallowed just the same: a camera that can never read anything, four errors a second, for
             * as long as the screen is open. `unsupported` is the honest answer, and the screen already
             * knows what to say — "enter the ID by hand", "ask a representative".
             */
            consecutiveFailures += 1;
            if (consecutiveFailures >= MAX_CONSECUTIVE_FRAME_FAILURES && live) {
              setState(SCAN_STATE.unsupported);
              stop();
            }
          }
        })();
      }, FRAME_INTERVAL_MS);
    };

    void start();
    return stop;
  }, [active, attempt]);

  return { state, value, videoRef, rescan };
}
